import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DocumentChartBarIcon, Cog8ToothIcon } from '@heroicons/react/24/outline';
import { ROUTES } from '../../consts/general';
import DateRangePicker from '../../components/reports/DateRangePicker';
import CSVExportButton from '../../components/reports/CSVExportButton';
import ReportTemplate from '../../components/reports/ReportTemplate';
import { ReportEmailSettingsSidebar } from '../../components/reports/ReportEmailSettingsSidebar';
import { format } from 'date-fns';
import { MONTHLY_PO_AP_DUE } from '../../consts/reports';
import { MONTHLY_PO_AP_DUE_TYPE } from '../../consts/cronTypes';
import { SelectDropdown } from '../../components/SelectDropdown';
import { useAbortableRequest } from '../../helpers/useAbortableRequest';
import { useInitialMountDelay } from '../../helpers/useInitialMountDelay';
import {
  getReportEmailSettings,
  updateReportEmailSettings,
  sendReportPreviewEmail,
} from '../../slices/reportEmailSettings';
import { getReportSendHistory } from '../../slices/reportSendHistory';
import { updateCronInformation } from '../../slices/cronSlice';
import { buildCronInterval } from '../../helpers/cronExpression';
import { toast } from 'react-toastify';
import useDateRangeParams from '../../hooks/useDateRangeParams';
import useUrlParam from '../../hooks/useUrlParam';

export const PurchaseOrderPaymentSchedule = ({
    marginFromSidebar
}) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { settings: allSettings, loading: isLoadingEmailSettings } = useSelector((s) => s.reportEmailSettings);
    const emailSettings = allSettings['monthly-po-ap-due'] || {};
    const { sendHistory: allSendHistory, loading: isLoadingSendHistory } = useSelector((s) => s.reportSendHistory);
    const sendHistory = allSendHistory['monthly-po-ap-due'] || [];
    const { beginRequest, isLatest } = useAbortableRequest();
    const initialReady = useInitialMountDelay(500);

    // State management
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPOs, setTotalPOs] = useState(0);
    const [startPO, setStartPO] = useState(1);
    const [endPO, setEndPO] = useState(0);
    const [uniquePOsHistory, setUniquePOsHistory] = useState({});
    const [isOpenSettings, setIsOpenSettings] = useState(false);
    const [totals, setTotals] = useState({
        deposit_1: '',
        deposit_2: '',
        balance_payment: '',
        credit_payment: '',
        shipping_payment: '',
        estimated_freight: '',
        total_due: '',
        forecast_remaining: '',
    });

    // Date filter state
    const defaultEndDate = new Date().toISOString().split('T')[0];
    const defaultStartDate = (() => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date.toISOString().split('T')[0];
    })();
    const { startDate, endDate, setDateRange } = useDateRangeParams(defaultStartDate, defaultEndDate);

    // Mode state: 'dates' | 'actual' and actualPeriod: 'all' | 'previous' | 'future'
    const [mode, setMode] = useUrlParam('mode', 'dates', (value) => ['dates', 'actual'].includes(value));
    const [actualPeriod, setActualPeriod] = useUrlParam(
        'actualPeriod',
        'all',
        (value) => ['all', 'previous', 'future'].includes(value),
    );

    // Sorting state
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState('asc');

    // PO number search
    const [poNumberSearch, setPoNumberSearch] = useState('');
    const [debouncedPoNumberSearch, setDebouncedPoNumberSearch] = useState('');

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedPoNumberSearch(poNumberSearch.trim());
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [poNumberSearch]);

    // Load email settings + send history on mount
    useEffect(() => {
        dispatch(getReportEmailSettings('monthly-po-ap-due'));
        dispatch(getReportSendHistory('monthly-po-ap-due'));
    }, [dispatch]);

    // Fetch data when filters, page, or sorting change
    useEffect(() => {
        if (!initialReady) return;
        if (mode === 'dates') {
            if (startDate && endDate) {
                fetchData();
            }
        } else {
            fetchData();
        }
    }, [initialReady, startDate, endDate, currentPage, sortField, sortDirection, mode, actualPeriod, debouncedPoNumberSearch]);

    // Update uniquePOsHistory when data changes
    useEffect(() => {
        setUniquePOsHistory(prev => ({
            ...prev,
            [currentPage]: uniquePOsOnPage.length
        }));
    }, [data, currentPage]);

    const fetchData = async () => {
        if (mode === 'dates' && (!startDate || !endDate)) return;

        const { signal, requestId } = beginRequest();
        setLoading(true);
        setError(null);

        try {
            // Build query parameters
            const params = new URLSearchParams({
                page: currentPage.toString(),
                pageSize: itemsPerPage.toString(),
                mode
            });

            if (mode === 'dates') {
                params.append('startDate', startDate);
                params.append('endDate', endDate);
            } else {
                params.append('actualPeriod', actualPeriod);
            }

            // Add sorting parameters if they exist
            if (sortField) {
                params.append('sortField', sortField);
                params.append('sortDirection', sortDirection);
            }

            if (debouncedPoNumberSearch) {
                params.append('poNumber', debouncedPoNumberSearch);
            }

            const response = await fetch(`/api/v1/reports/${MONTHLY_PO_AP_DUE}?${params.toString()}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                },
                signal
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to fetch report data');
            }

            const responseData = await response.json();
            if (!isLatest(requestId)) return;
            setData(responseData.data || []);
            setTotalRecords(responseData.totalRecords || 0);
            setTotalPages(responseData.totalPages || 1);
            setTotalPOs(responseData.totalPOs || 0);
            setStartPO(responseData.startPO || 1);
            setEndPO(responseData.endPO || 0);
            setTotals(responseData.totals || {
                deposit_1: '',
                deposit_2: '',
                balance_payment: '',
                credit_payment: '',
                shipping_payment: '',
                total_due: ''
            });
        } catch (err) {
            if (err?.name === 'AbortError') return;
            console.error('Error fetching report data:', err);
            if (!isLatest(requestId)) return;
            setError(err.message);
            setData([]);
        } finally {
            if (isLatest(requestId)) setLoading(false);
        }
    };

    const handleDateChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
        setDateRange({ startDate: newStartDate, endDate: newEndDate });
        setCurrentPage(1); // Reset to first page when dates change
    };

    const handleModeChange = (valueOrEvent) => {
        const newMode = typeof valueOrEvent === 'string' ? valueOrEvent : valueOrEvent?.target?.value;
        setMode(newMode);
        setCurrentPage(1);
    };

    const handleActualPeriodChange = (e) => {
        setActualPeriod(e.target.value);
        setCurrentPage(1);
    };

    const handlePoNumberSearchChange = (e) => {
        setPoNumberSearch(e.target.value);
        setCurrentPage(1);
    };

    const handleSort = ({ field, direction }) => {
        setSortField(field);
        setSortDirection(direction);
        setCurrentPage(1); // Reset to first page when sorting changes
    };

    const handleBackToReports = () => {
        navigate(ROUTES.REPORTS);
    };

    const isValidNumber = (value) => {
        if (value === null || value === undefined) return false;
        const numValue = typeof value === 'string' ? value.replace(/[^0-9.-]/g, '') : value;
        return !isNaN(parseFloat(numValue));
    };

    const formatCurrency = (value) => {
        if (!isValidNumber(value)) return '';
        const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : parseFloat(value);
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
            useGrouping: true
        }).format(numValue);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return format(date, 'MMM dd, yyyy');
    };

    // Calculate totals for the current page
    const calculateTotals = () => {
        return data.reduce((acc, row) => ({
            deposit_1: acc.deposit_1 + (isValidNumber(row.deposit_1) ? parseFloat(row.deposit_1) : 0),
            deposit_2: acc.deposit_2 + (isValidNumber(row.deposit_2) ? parseFloat(row.deposit_2) : 0),
            balance_payment: acc.balance_payment + (isValidNumber(row.balance_payment) ? parseFloat(row.balance_payment) : 0),
            credit_payment: acc.credit_payment + (isValidNumber(row.credit_payment) ? parseFloat(row.credit_payment) : 0),
            shipping_payment: acc.shipping_payment + (isValidNumber(row.shipping_payment) ? parseFloat(row.shipping_payment) : 0),
            estimated_freight: acc.estimated_freight + (isValidNumber(row.estimated_freight) ? parseFloat(row.estimated_freight) : 0),
            total_due: acc.total_due + (isValidNumber(row.total_due) ? parseFloat(row.total_due) : 0),
            forecast_remaining: acc.forecast_remaining + (isValidNumber(row.forecast_remaining) ? parseFloat(row.forecast_remaining) : 0),
        }), {
            deposit_1: 0,
            deposit_2: 0,
            balance_payment: 0,
            credit_payment: 0,
            shipping_payment: 0,
            estimated_freight: 0,
            total_due: 0,
            forecast_remaining: 0,
        });
    };

    // Helper to get unique PO numbers from data
    const getUniquePOs = (data) => {
        const set = new Set();
        data.forEach(row => set.add(row.po_number));
        return Array.from(set).filter(Boolean);
    };

    // Unique POs for current page
    const uniquePOsOnPage = getUniquePOs(data);

    // Breadcrumbs configuration
    const breadcrumbs = [
        {
            label: 'Reports',
            icon: <DocumentChartBarIcon className="h-5 w-5 text-gray-400" />
        },
        {
            label: ''
        }
    ];

    // Filters component
    const filtersComponent = (
        <div className="contents">
            <div className="flex flex-row items-center gap-2">
                <input
                    type="text"
                    value={poNumberSearch}
                    onChange={handlePoNumberSearchChange}
                    placeholder="Search PO #"
                    className="w-40 sm:w-[150px] rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
            </div>

            <div className="flex flex-row items-center gap-2">
                <label className="text-xs font-medium text-gray-600">Mode:</label>
                <div className="min-w-[160px]">
                    <SelectDropdown
                        options={[
                            { label: 'Date range', value: 'dates' },
                            { label: 'Period', value: 'actual' }
                        ]}
                        value={mode}
                        selectClassname="!p-2 !bg-white"
                        onChange={handleModeChange}
                        placeholder="By"
                    />
                </div>
            </div>

            {mode === 'actual' && (
                <fieldset className="flex flex-col">
                    <div className="flex items-center gap-4">
                        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                            <input
                                type="radio"
                                name="actualPeriod"
                                value="all"
                                checked={actualPeriod === 'all'}
                                onChange={handleActualPeriodChange}
                                className="h-4 w-4 text-indigo-600 border-gray-300"
                            />
                            All
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                            <input
                                type="radio"
                                name="actualPeriod"
                                value="previous"
                                checked={actualPeriod === 'previous'}
                                onChange={handleActualPeriodChange}
                                className="h-4 w-4 text-indigo-600 border-gray-300"
                            />
                            Previous
                        </label>
                        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                            <input
                                type="radio"
                                name="actualPeriod"
                                value="future"
                                checked={actualPeriod === 'future'}
                                onChange={handleActualPeriodChange}
                                className="h-4 w-4 text-indigo-600 border-gray-300"
                            />
                            Future
                        </label>
                    </div>
                </fieldset>
            )}

            {mode === 'dates' && (
                <DateRangePicker
                    startDate={startDate}
                    endDate={endDate}
                    onDateChange={handleDateChange}
                    label="Date Range"
                    summaryDateFormat="MM/dd/yyyy"
                    pickerDateFormat="MM/dd/yyyy"
                    error={error && error.includes('date') ? error : null}
                />
            )}
        </div>
    );

    // Export button component
    const exportButton = (
        <CSVExportButton
            data={data}
            columns={[
                { key: 'po_number', label: 'PO Number', type: 'string' },
                { key: 'date', label: 'Due Date', type: 'date' },
                { key: 'deposit_1', label: 'Deposit 1', type: 'currency' },
                { key: 'deposit_2', label: 'Deposit 2', type: 'currency' },
                { key: 'balance_payment', label: 'Balance Payment', type: 'currency' },
                { key: 'credit_payment', label: 'Credit Payment', type: 'currency' },
                { key: 'shipping_payment', label: 'Shipping Payment', type: 'currency' },
                { key: 'estimated_freight', label: 'Est. Freight (not in Total Due)', type: 'currency' },
                { key: 'total_due', label: 'Total Due', type: 'currency' },
                { key: 'forecast_remaining', label: 'Projected Cash Out', type: 'currency' },
                { key: 'exceptions', label: 'Exceptions', type: 'string' }
            ]}
            filename={`${MONTHLY_PO_AP_DUE}`}
            onExport={async () => {
                try {
                    // Build query parameters for export
                    const params = new URLSearchParams({ mode });
                    if (mode === 'dates') {
                        params.append('startDate', startDate);
                        params.append('endDate', endDate);
                    } else {
                        params.append('actualPeriod', actualPeriod);
                    }

                    // Add sorting parameters if they exist
                    if (sortField) {
                        params.append('sortField', sortField);
                        params.append('sortDirection', sortDirection);
                    }

                    if (debouncedPoNumberSearch) {
                        params.append('poNumber', debouncedPoNumberSearch);
                    }

                    const response = await fetch(`/api/v1/reports/${MONTHLY_PO_AP_DUE}/export?${params.toString()}`, {
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('token')}`,
                            'Content-Type': 'application/json'
                        }
                    });

                    if (!response.ok) {
                        const errorData = await response.json();
                        throw new Error(errorData.error || 'Failed to export report data');
                    }

                    // Get the filename from the response headers
                    const contentDisposition = response.headers.get('content-disposition');
                    const filename = contentDisposition 
                        ? contentDisposition.split('filename=')[1]?.replace(/"/g, '') 
                        : `${MONTHLY_PO_AP_DUE}.csv`;

                    // Create blob and download
                    const blob = await response.blob();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = filename;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);

                    return { success: true, handled: true };
                } catch (error) {
                    console.error('Export error:', error);
                    throw error;
                }
            }}
        />
    );

    // Pagination component
    const paginationComponent = totalPages > 1 ? (
        <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
                {totalPOs > 0 ? (
                    (() => {
                        let startPO = 1;
                        for (let i = 1; i < currentPage; i++) {
                            startPO += uniquePOsHistory[i] || 0;
                        }
                        const endPO = Math.min(startPO + uniquePOsOnPage.length - 1, totalPOs);
                        return `Showing ${startPO}-${endPO} of ${totalPOs} POs`;
                    })()
                ) : (
                    'No POs found'
                )}
            </div>
            <div className="flex items-center space-x-2">
                <button
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                    Previous
                </button>
                <span className="text-sm text-gray-700">
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:bg-gray-100 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                    Next
                </button>
            </div>
        </div>
    ) : null;

    // Render total row
    const renderTotalRow = () => {
        return (
            <tr className="bg-gray-100 font-bold border-b border-gray-200">
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900"><div/></td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 bg-white"><div/></td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right">{totals.deposit_1 ? `$${totals.deposit_1}` : ""}</td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right">{totals.deposit_2 ? `$${totals.deposit_2}` : ""}{}</td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right">{totals.balance_payment ? `$${totals.balance_payment}` : ""}</td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right">{totals.credit_payment ? `$${totals.credit_payment}` : ""}</td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right">{totals.shipping_payment ? `$${totals.shipping_payment}` : ""}</td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right">{totals.estimated_freight ? `$${totals.estimated_freight}` : ""}</td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right">{totals.total_due ? `$${totals.total_due}` : ""}</td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right">{totals.forecast_remaining ? `$${totals.forecast_remaining}` : ""}</td>
                <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 text-right"><div/></td>
            </tr>
        );
    };

    const handleSuccessSubmit = async (formData) => {
        const payload = {
            emailRecipients: formData?.emailRecipients || [],
            scheduleType: formData?.schedule || null,
            dayOfMonth: formData?.dayOfMonth || null,
            slackChannelId: formData?.slackChannelId || null,
            dayOfWeek: formData?.dayOfWeek || null,
            timeOfDay: formData?.timeOfDay || null,
            emailMessage: formData?.emailMessage || null,
        };
        try {
            await dispatch(updateReportEmailSettings({ reportType: 'monthly-po-ap-due', ...payload })).unwrap();
            const cronInterval = buildCronInterval({
                scheduleType: payload.scheduleType,
                dayOfMonth: payload.dayOfMonth,
                dayOfWeek: payload.dayOfWeek,
                timeOfDay: payload.timeOfDay,
            });
            if (!cronInterval) {
                toast.error('Invalid schedule settings (cron expression could not be built)', { position: 'bottom-right' });
                return;
            }
            const cronData = {
                id: emailSettings?.cronId,
                type: emailSettings?.cronType || MONTHLY_PO_AP_DUE_TYPE,
                cronInterval,
            };
            await dispatch(updateCronInformation({ singleData: cronData })).unwrap();
            toast.success('Settings saved successfully', { position: 'bottom-right' });
            setIsOpenSettings(false);
        } catch (e) {
            toast.error(typeof e === 'string' ? e : 'Failed to save settings', { position: 'bottom-right' });
        }
    };

    const sendPreviewEmail = async (formData) => {
        try {
            const payload = {
                email: formData?.emailSendPreviewTo,
                emailMessage: formData?.emailMessage || '',
                type: formData?.type || null,
                startDate: startDate,
                endDate: endDate,
                sortField: sortField || null,
                sortDirection: sortDirection || null,
                mode: mode,
                actualPeriod: actualPeriod,
                poNumber: debouncedPoNumberSearch || null,
            };
            await dispatch(sendReportPreviewEmail({ reportType: 'monthly-po-ap-due', ...payload })).unwrap();
            await dispatch(getReportSendHistory('monthly-po-ap-due')).unwrap();
            toast.success('Preview email sent', { position: 'bottom-right' });
        } catch (e) {
            toast.error(typeof e === 'string' ? e : 'Failed to send preview', { position: 'bottom-right' });
        }
    };

    const sendPreviewSlack = async (formData) => {
        try {
            const payload = {
                slackChannelId: formData?.slackChannelId,
                emailMessage: formData?.emailMessage || '',
                type: formData?.type || null,
                startDate: startDate,
                endDate: endDate,
                sortField: sortField || null,
                sortDirection: sortDirection || null,
                mode: mode,
                actualPeriod: actualPeriod,
                poNumber: debouncedPoNumberSearch || null,
            };
            await dispatch(sendReportPreviewEmail({ reportType: 'monthly-po-ap-due', ...payload })).unwrap();
            await dispatch(getReportSendHistory('monthly-po-ap-due')).unwrap();
            toast.success('Slack preview sent', { position: 'bottom-right' });
        } catch (e) {
            toast.error(typeof e === 'string' ? e : 'Failed to send slack preview', { position: 'bottom-right' });
        }
    };

    // Render table rows
    const renderTableRows = () => {
        return data.map((row, index) => (
            <tr key={index} className={row.po_number ? 'bg-white' : 'bg-gray-50'}>
                <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${row.po_number ? 'bg-white' : '!bg-gray-50'}`}>
                    {row.po_number}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(row.date)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(row.deposit_1)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(row.deposit_2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(row.balance_payment)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(row.credit_payment)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(row.shipping_payment)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(row.estimated_freight)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(row.total_due)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                    {formatCurrency(row.forecast_remaining)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {row.exception_count > 0 ? (
                        <span>
                            {row.exceptions || 'Exceptions'} <span className="rounded bg-red-100 px-1.5 py-0.5 text-xs text-red-700">{row.exception_count}</span>
                        </span>
                    ) : row.exceptions || ''}
                </td>
            </tr>
        ));
    };

    return (
        <>
            <ReportTemplate
                reportId={MONTHLY_PO_AP_DUE}
                marginFromSidebar={marginFromSidebar}
                description="Payment schedule by Purchase Order including deposits, balance, credits, and shipping payments from the accounts payable system"
                breadcrumbs={breadcrumbs}
                onBack={handleBackToReports}
                isLoading={loading}
                error={error}
                filters={filtersComponent}
                exportButton={
                    <div className="flex items-center space-x-2">
                        {exportButton}
                        <div>
                            <Cog8ToothIcon
                                className="h-5 w-5 text-primary cursor-pointer"
                                onClick={() => {
                                setIsOpenSettings(true);
                                }}
                            />
                        </div>
                    </div>
                }
                data={data}
                cronType={emailSettings?.cronType || null}
                columns={[
                    { key: 'po_number', label: 'PO Number', type: "string", sortable: true, sticky: true },
                    { key: 'date', label: 'Due Date', type: "date", sortable: true },
                    { key: 'deposit_1', label: 'Deposit 1', type: "currency", sortable: true },
                    { key: 'deposit_2', label: 'Deposit 2', type: "currency", sortable: true },
                    { key: 'balance_payment', label: 'Balance', type: "currency", sortable: true },
                    { key: 'credit_payment', label: 'Credit', type: "currency", sortable: true },
                    { key: 'shipping_payment', label: 'Shipping', type: "currency", sortable: true },
                    { key: 'estimated_freight', label: 'Est. Freight (not in Total Due)', type: "currency", sortable: true },
                    { key: 'total_due', label: 'Total Due', type: "currency", sortable: true },
                    { key: 'forecast_remaining', label: 'Projected Cash Out', type: "currency", sortable: true },
                    { key: 'exceptions', label: 'Exceptions', type: "string", sortable: false }
                ]}
                pagination={paginationComponent}
                emptyMessage="No payment data found for the selected date range. Try selecting a different date range or check if there are payments in the system."
                onSort={handleSort}
                sortField={sortField}
                sortDirection={sortDirection}
                enableSorting={true}
            >
                <>
                    {renderTotalRow()}
                    {renderTableRows()}
                </>
            </ReportTemplate>
            <ReportEmailSettingsSidebar
        reportType={'monthly-po-ap-due'}
                isOpen={isOpenSettings}
                marginFromSidebar={marginFromSidebar}
                onClose={() => setIsOpenSettings(false)}
                isLoading={isLoadingEmailSettings}
                onSuccessSubmit={handleSuccessSubmit}
                sendPreviewEmail={sendPreviewEmail}
                sendPreviewSlack={sendPreviewSlack}
                defaultSettings={emailSettings || {}}
                sendHistory={sendHistory}
                isLoadingSendHistory={isLoadingSendHistory}
                downloadFilenamePrefix="monthly-po-ap-due"
            />
        </>
    );
}; 